import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdPerson,
  MdSupervisorAccount,
  MdAdminPanelSettings,
  MdFilterList,
  MdSearch,
  MdRefresh,
} from "react-icons/md";
import { useWorkers, ROLES, ROLE_LABELS } from "context/WorkersContext";
import { useSectorPersonnel } from "context/SectorPersonnelContext";
import { getSectors } from "api/sectors";
import AddWorker from "views/performanceKPI/AddWorker";

const SECONDARY = "#f27405";

const ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: ROLES.ADMIN, label: ROLE_LABELS[ROLES.ADMIN] },
  { value: ROLES.SUPERVISOR, label: ROLE_LABELS[ROLES.SUPERVISOR] },
  { value: ROLES.OPERATOR, label: ROLE_LABELS[ROLES.OPERATOR] },
];

function RoleBadge({ role }) {
  const config = {
    [ROLES.ADMIN]: {
      Icon: MdAdminPanelSettings,
      style: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300",
    },
    [ROLES.SUPERVISOR]: {
      Icon: MdSupervisorAccount,
      style: "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
    },
    [ROLES.OPERATOR]: {
      Icon: MdPerson,
      style: "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
    },
  };
  const { Icon, style } = config[role] || config[ROLES.OPERATOR];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${style}`}
    >
      <Icon className="text-sm" />
      {ROLE_LABELS[role] || role}
    </span>
  );
}

export default function Workers() {
  const { workers, addWorker, updateWorker, removeWorker, loading, error, refetchWorkers } = useWorkers();
  const { setSectorPersonnel } = useSectorPersonnel();
  const [sectorsList, setSectorsList] = useState([]);
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editWorker, setEditWorker] = useState(null);

  const fetchSectors = useCallback(async () => {
    try {
      const result = await getSectors();
      setSectorsList(result.data || []);
    } catch {
      setSectorsList([]);
    }
  }, []);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  // Sync sectorPersonnel from workers (API provides sector_name per user)
  useEffect(() => {
    if (sectorsList.length === 0) return;
    const next = {};
    sectorsList.forEach((s) => {
      next[s.id] = { supervisorIds: [], operatorIds: [] };
    });
    workers.forEach((w) => {
      if (!w.sector_name) return;
      const sector = sectorsList.find((s) => s.name === w.sector_name);
      if (!sector) return;
      const p = next[sector.id] || { supervisorIds: [], operatorIds: [] };
      if (w.role === ROLES.SUPERVISOR) {
        p.supervisorIds = [...(p.supervisorIds || []), w.id];
      } else if (w.role === ROLES.OPERATOR) {
        p.operatorIds = [...(p.operatorIds || []), w.id];
      }
      next[sector.id] = p;
    });
    setSectorPersonnel(next);
  }, [workers, sectorsList, setSectorPersonnel]);

  const filteredWorkers = useMemo(() => {
    let list = workers;
    if (roleFilter) {
      list = list.filter((w) => w.role === roleFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (w) =>
          w.name.toLowerCase().includes(q) ||
          (w.email && w.email.toLowerCase().includes(q)) ||
          (w.employeeId && w.employeeId.toLowerCase().includes(q))
      );
    }
    return list;
  }, [workers, roleFilter, search]);

  return (
    <div className="p-4 md:p-6 pt-4 md:pt-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-slate-900 dark:text-white text-xl sm:text-2xl md:text-3xl font-black tracking-tight">
            Workers
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Manage the worker directory. Add workers with roles (Admin, Supervisor, Operator) so they can be assigned to sectors.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={refetchWorkers}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-60"
            title="Refresh"
          >
            <MdRefresh className={`text-lg ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setEditWorker(null);
              setAddModalOpen(true);
            }}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-black shadow-lg hover:opacity-90 transition-opacity shrink-0"
            style={{ backgroundColor: SECONDARY }}
          >
            <MdAdd className="text-lg" />
            Add worker
          </button>
        </div>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-12 text-center">
          <p className="text-slate-500 text-sm">Loading workers…</p>
        </div>
      )}
      {error && !loading && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 flex items-center justify-between gap-4">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          <button
            type="button"
            onClick={refetchWorkers}
            className="px-4 py-2 rounded-lg bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm font-bold hover:bg-red-200 dark:hover:bg-red-900/60"
          >
            Retry
          </button>
        </div>
      )}

      {/* Filters - hide when loading or error */}
      {!loading && !error && (
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 min-w-0">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or employee ID…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-transparent focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <MdFilterList className="text-slate-500 shrink-0" />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-sm font-medium px-4 py-2.5 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-[#0e76a8]/30"
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      )}

      {/* Table (hidden on small screens; use cards below) */}
      {!loading && !error && (
      <div className="rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm hidden sm:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-black uppercase tracking-widest text-slate-500">
                  Name
                </th>
                <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-black uppercase tracking-widest text-slate-500">
                  Role
                </th>
                <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-black uppercase tracking-widest text-slate-500 hidden sm:table-cell">
                  Sector(s)
                </th>
                <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-black uppercase tracking-widest text-slate-500 hidden lg:table-cell">
                  Email
                </th>
                <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-black uppercase tracking-widest text-slate-500 hidden xl:table-cell">
                  Employee ID
                </th>
                <th className="px-4 sm:px-6 py-3.5 text-right text-xs font-black uppercase tracking-widest text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredWorkers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 sm:px-6 py-12 text-center text-slate-500 text-sm">
                    {workers.length === 0
                      ? "No workers yet. Add a worker to get started."
                      : "No workers match your filters."}
                  </td>
                </tr>
              ) : (
                filteredWorkers.map((worker) => (
                  <tr
                    key={worker.id}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3.5 font-semibold text-slate-900 dark:text-white">
                      {worker.name}
                    </td>
                    <td className="px-4 sm:px-6 py-3.5">
                      <RoleBadge role={worker.role} />
                    </td>
                    <td className="px-4 sm:px-6 py-3.5 text-slate-600 dark:text-slate-400 hidden sm:table-cell">
                      {worker.sector_name || "—"}
                    </td>
                    <td className="px-4 sm:px-6 py-3.5 text-slate-600 dark:text-slate-400 hidden lg:table-cell">
                      {worker.email || "—"}
                    </td>
                    <td className="px-4 sm:px-6 py-3.5 text-slate-600 dark:text-slate-400 hidden xl:table-cell">
                      {worker.employeeId || "—"}
                    </td>
                    <td className="px-4 sm:px-6 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setAddModalOpen(false);
                            setEditWorker(worker);
                          }}
                          className="p-2 rounded-lg text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-[#0e76a8] transition-colors"
                          title="Edit"
                        >
                          <MdEdit className="text-lg" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Remove ${worker.name} from the directory?`)) {
                              removeWorker(worker.id);
                            }
                          }}
                          className="p-2 rounded-lg text-slate-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 transition-colors"
                          title="Remove"
                        >
                          <MdDelete className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Mobile cards */}
      {!loading && !error && (
      <div className="sm:hidden space-y-3">
        {filteredWorkers.length === 0 ? (
          <p className="text-center text-slate-500 text-sm py-8">
            {workers.length === 0
              ? "No workers yet. Add a worker to get started."
              : "No workers match your filters."}
          </p>
        ) : filteredWorkers.map((worker) => (
          <div
            key={worker.id}
            className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50/30 dark:bg-slate-800/30"
          >
            <div className="flex justify-between items-start gap-3 mb-2">
              <h3 className="font-bold text-slate-900 dark:text-white">{worker.name}</h3>
              <RoleBadge role={worker.role} />
            </div>
            {worker.sector_name ? (
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                Sector(s): {worker.sector_name}
              </p>
            ) : null}
            {worker.email ? (
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">{worker.email}</p>
            ) : null}
            {worker.employeeId ? (
              <p className="text-xs text-slate-500 dark:text-slate-500 mb-2">
                ID: {worker.employeeId}
              </p>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditWorker(worker)}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-[#0e76a8]"
              >
                <MdEdit className="text-lg" />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Remove ${worker.name}?`)) removeWorker(worker.id);
                }}
                className="p-2 rounded-lg text-slate-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600"
              >
                <MdDelete className="text-lg" />
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      <AddWorker
        open={addModalOpen || Boolean(editWorker)}
        onClose={() => {
          setAddModalOpen(false);
          setEditWorker(null);
        }}
        onSuccess={(payload) => {
          let workerId;
          if (editWorker) {
            updateWorker(payload.id, payload);
            workerId = payload.id;
            setEditWorker(null);
          } else {
            workerId = addWorker(payload);
            setAddModalOpen(false);
          }
          const sectorIds = (payload.sectorIds || []).filter(Boolean);
          const role = payload.role;
          const isSupervisor = role === ROLES.SUPERVISOR;
          const isOperator = role === ROLES.OPERATOR;
          if (workerId && sectorsList.length > 0) {
            setSectorPersonnel((prev) => {
              const next = {};
              sectorsList.forEach((s) => {
                const p = prev[s.id] || { supervisorIds: [], operatorIds: [] };
                let sup = (p.supervisorIds || []).filter((id) => id !== workerId);
                let op = (p.operatorIds || []).filter((id) => id !== workerId);
                const inSector = sectorIds.includes(s.id) && (isSupervisor || isOperator);
                if (inSector) {
                  if (isSupervisor) sup = [...sup, workerId];
                  if (isOperator) op = [...op, workerId];
                }
                next[s.id] = { supervisorIds: sup, operatorIds: op };
              });
              return { ...prev, ...next };
            });
          }
        }}
        editWorker={editWorker}
      />
    </div>
  );
}
